import React from 'react'
import SvgMail from '#/assets/images/card/mail_outline.svg'
import Button from '#/components/Button'
import OverlaySpinner from '#/components/OverlaySpinner'
import TextInput, { TextArea } from '#/components/TextInput'
import { logEvent } from '#/services/analytics'
import { RequestInfo as RequestInfoAction } from '#/store/actions/user'
import cn from '#/utils/classnames'
import { useInputState } from '#/utils/hook'
import { showSuccess } from '#/utils/message'
import { viewUrl } from '#/utils/properties'

interface RequestInfoProps {
  asyncStatus: TopHap.GlobalState['status']
  property: TopHap.Property
  small?: boolean
  agent?: TopHap.UserInfo
  user?: TopHap.UserInfo
  onClose?: () => void
  requestInfo: typeof RequestInfoAction
}

export default function RequestInfo({
  asyncStatus,
  property,
  small,
  agent,
  user,
  onClose,
  requestInfo
}: RequestInfoProps) {
  const [name, setName] = useInputState(user?.displayName || '')
  const [phone, setPhone] = useInputState(user?.phoneNumber || '')
  const [email, setEmail] = useInputState(user?.email || '')
  const address = `${property.displayAddress}, ${property.displayRegion}`
  const [message, setMessage] = useInputState(
    ['Sold', 'PrivateSale'].includes(property.TophapStatus) ?
      `I am interested in selling ${address}` :
      `I am interested in ${address}`
  )
  const isWaiting = React.useRef(false)

  React.useEffect(() => {
    if (asyncStatus[RequestInfoAction.type] === 'success' && isWaiting.current) {
      (async () => {
        await showSuccess({
          title: 'Success',
          content: 'Your message is sent.'
        })
      })()

      isWaiting.current = false

      if (onClose) onClose()
    }
  }, [asyncStatus[RequestInfoAction.type]])

  function handleSubmit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault()

    const payload = {
      name,
      phone,
      email,
      message,
      property: {
        address: property.address.FullAddress,
        link: viewUrl(property.id, property.address),
        status: property.TophapStatus,
        isListHub: property.isListHub,
        id: property.id
      }
    }

    requestInfo({
      to: agent?.email,
      payload
    })

    isWaiting.current = true

    logEvent('generate_lead', {
      content_type: 'request_info',
      value: property.id,
      address: property.address.FullAddress,
      listhub_key: property.isListHub ? property.ListingKey : undefined,
      name,
      phone,
      email,
      message,
      status
    })
  }

  return (
    <form
      className={cn(
        'grid relative mb-3',
        small ? 'grid-cols-1' : 'grid-cols-2 gap-4'
      )}
      onSubmit={handleSubmit}
    >
      <div>
        <TextInput
          className='my-1 w-full'
          icon='person'
          label='Name'
          placeholder='Name'
          required
          value={name}
          onChange={setName}
        />
        <TextInput
          className='my-1 w-full'
          icon='phone'
          label='Phone'
          placeholder='Phone'
          type='phone'
          value={phone}
          onChange={setPhone}
        />
        <TextInput
          className='my-1 w-full'
          icon='mail'
          label='Email'
          placeholder='Email'
          required
          type='email'
          value={email}
          onChange={setEmail}
        />
      </div>
      <div className={cn({ 'mt-1': !small })}>
        <TextArea
          className='w-full'
          label='Message'
          required
          rows={small ? 4 : 6}
          value={message}
          onChange={setMessage}
        />
        <div className='flex w-full justify-end'>
          <Button className='my-2 h-8 rounded bg-secondary px-4 text-xs font-medium text-white' type='submit'>
            <SvgMail className='mr-1 h-[18px] w-[18px]' />
            {' '}
            Request Info
          </Button>
        </div>
      </div>

      <OverlaySpinner
        absolute
        visible={asyncStatus[RequestInfoAction.type] === 'request'}
      />
    </form>
  )
}
